import React, { useEffect, useState } from "react";
import { getSettings, setSettings, Settings } from "../../shared/settings";
import { inAndroidApp } from "../../shared/app-shell";
import { buzz, canVibrate, testVibration, onAppBridge, VIBRATION_STRENGTHS, VIBRATION_TEST_IDLE } from "../../shared/ui-feedback";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../shared/auth";
import { isAdmin } from "../../shared/admin";
import { isTestAccountEmail } from "core/src/shop/admins.js";
import { resetTestAccount, startAsFreshGuest } from "../../shared/fresh-start";
import { fullscreenSupported, toggleFullscreen, useFullscreen } from "../../shared/fullscreen";
import { useIsTouch } from "../../shared/useResponsive";
import KeyPicker from "../../shared/key-picker/KeyPicker";
import { useKeyChainFocus } from "../../shared/useKeyChainFocus";
import { useLocalRoster } from "../lobby/start/hooks";
import styles from "./settings.module.scss";

export default function SettingsPage() {
  const [s, setS] = useState<Settings>(() => getSettings());
  const app = inAndroidApp();
  // The admin pages are reached from here, so the bar is the same for everyone.
  const navigate = useNavigate();
  const { user } = useAuth();
  const admin = isAdmin(user);
  const testAccount = isTestAccountEmail(user?.email);
  // Starting over is asked twice: the first press arms the button.
  const [armed, setArmed] = useState<"reset" | "guest" | null>(null);
  const [resetNote, setResetNote] = useState("");
  // Full screen now, and the steering keys: the same ones the navbar's
  // fullscreen icon and the player menu set, found here as well.
  const fullscreen = useFullscreen();
  const roster = useLocalRoster();
  const touch = useIsTouch(); // a touch device, not a narrow window: a small PC window keeps its keys
  const { advance, focusToken } = useKeyChainFocus();
  // What the phone answered to Test vibration.
  const [vibeNote, setVibeNote] = useState(VIBRATION_TEST_IDLE);
  // the Android app's haptics line opens a moment after the page: show its rows then
  const [, bridged] = useState(0);
  useEffect(() => onAppBridge(() => bridged((n) => n + 1)), []);

  function update(patch: Partial<Settings>) {
    const next = { ...s, ...patch };
    setS(next);
    setSettings(next);
  }

  return (
    <div className={`section ${styles.settingsPage}`}>
      <div className={styles.settingsGroup}>
        <h2 className={styles.settingsH}>Sound</h2>
        <label className={styles.settingsRow}>
          <div className={styles.settingsMain}>
            <span className={styles.settingsName}>Sound</span>
            <span className={styles.settingsDesc}>Music and gameplay effects.</span>
          </div>
          <input
            className={styles.settingsToggle}
            type="checkbox"
            checked={!s.muted}
            onChange={(e) => update({ muted: !e.target.checked })}
          />
        </label>
        <label className={styles.settingsRow}>
          <div className={styles.settingsMain}>
            <span className={styles.settingsName}>Music volume</span>
            <span className={styles.settingsDesc}>Level of the background music.</span>
          </div>
          <input
            type="range"
            min={0}
            max={100}
            value={s.volume}
            disabled={s.muted}
            onChange={(e) => update({ volume: Number(e.target.value) })}
          />
          <span className={`${styles.settingsVal} app-muted`}>{s.muted ? "off" : s.volume + "%"}</span>
        </label>
      </div>
      <div className={styles.settingsGroup}>
        <h2 className={styles.settingsH}>{app ? "Phone" : "Screen"}</h2>
        {/* the app is always full screen */}
        {!app && fullscreenSupported() && (
          <label className={styles.settingsRow}>
            <div className={styles.settingsMain}>
              <span className={styles.settingsName}>Full screen</span>
              <span className={styles.settingsDesc}>The game fills the whole screen, without the browser around it.</span>
            </div>
            <input className={styles.settingsToggle} type="checkbox" checked={fullscreen} onChange={toggleFullscreen} />
          </label>
        )}
        {!app && (
          <label className={styles.settingsRow}>
            <div className={styles.settingsMain}>
              <span className={styles.settingsName}>Full screen when a match starts</span>
              <span className={styles.settingsDesc}>Go full screen as a match begins and stay there.</span>
            </div>
            <input
              className={styles.settingsToggle}
              type="checkbox"
              checked={s.autoFullscreen}
              onChange={(e) => update({ autoFullscreen: e.target.checked })}
            />
          </label>
        )}
        {canVibrate() && (
          <label className={styles.settingsRow}>
            <div className={styles.settingsMain}>
              <span className={styles.settingsName}>Vibration</span>
              <span className={styles.settingsDesc}>A short buzz when you press a button.</span>
            </div>
            <input
              className={styles.settingsToggle}
              type="checkbox"
              checked={s.vibration}
              onChange={(e) => update({ vibration: e.target.checked })}
            />
          </label>
        )}
        {canVibrate() && s.vibration && (
          <div className={styles.settingsRow}>
            <div className={styles.settingsMain}>
              <span className={styles.settingsName}>Strength</span>
              <span className={styles.settingsDesc}>Tap one to feel it.</span>
            </div>
            <div className={styles.settingsChips} data-haptic="none">
              {VIBRATION_STRENGTHS.map((v) => (
                <button key={v.id} type="button" data-sound="toggle" aria-pressed={s.vibrationStrength === v.id} className={s.vibrationStrength === v.id ? styles.settingsChipOn : styles.settingsChip} onClick={() => { update({ vibrationStrength: v.id }); buzz(false, v.id); }}>{v.label}</button>
              ))}
            </div>
          </div>
        )}
        {app && (
          <div className={styles.settingsRow}>
            <div className={styles.settingsMain}>
              <span className={styles.settingsName}>Test vibration</span>
              <span className={styles.settingsDesc}>{vibeNote}</span>
            </div>
            <button className="app-btn app-btn-primary" data-sound="none" data-haptic="none" onClick={() => setVibeNote(testVibration())}>Test</button>
          </div>
        )}
      </div>
      {!touch && (
        <div className={styles.settingsGroup}>
          <h2 className={styles.settingsH}>Controls</h2>
          {roster.players.map((p, i) => (
            <div key={p.slot} className={styles.settingsRow}>
              <div className={styles.settingsMain}>
                <span className={styles.settingsName}>{roster.players.length > 1 ? p.name : "Steering"}</span>
                <span className={styles.settingsDesc}>Turn left, turn right.</span>
              </div>
              <div className={styles.settingsKeys}>
                <KeyPicker currentKey={p.left ?? undefined} onKeyPicked={(k: string) => { roster.setKey(i, "left", k); advance(String(i), "left"); }} autoSelectToken={focusToken(String(i), "left")} />
                <KeyPicker currentKey={p.right ?? undefined} onKeyPicked={(k: string) => { roster.setKey(i, "right", k); advance(String(i), "right"); }} autoSelectToken={focusToken(String(i), "right")} />
              </div>
            </div>
          ))}
        </div>
      )}
      {admin && (
        <div className={styles.settingsGroup}>
          <h2 className={styles.settingsH}>Admin</h2>
          <div className={styles.settingsRow}>
            <div className={styles.settingsMain}>
              <span className={styles.settingsName}>Admin pages</span>
              <span className={styles.settingsDesc}>Stats, bots, replays, marketing and the labs.</span>
            </div>
            <button className="app-btn app-btn-primary" onClick={() => navigate("/admin")}>Open</button>
          </div>
          {!user && (
            <div className={styles.settingsRow}>
              <div className={styles.settingsMain}>
                <span className={styles.settingsName}>Play as a new guest</span>
                <span className={styles.settingsDesc}>Forget everything this device remembers, like a first visit.</span>
              </div>
              <button className="app-btn app-btn-ghost" onClick={() => (armed === "guest" ? void startAsFreshGuest() : setArmed("guest"))}>{armed === "guest" ? "Sure? Start over" : "Start over"}</button>
            </div>
          )}
        </div>
      )}
      {(admin || testAccount) && user && (
        <div className={styles.settingsGroup}>
          <h2 className={styles.settingsH}>New player</h2>
          {testAccount ? (
            <div className={styles.settingsRow}>
              <div className={styles.settingsMain}>
                <span className={styles.settingsName}>Start this account over</span>
                <span className={styles.settingsDesc}>{resetNote || "Deletes its ratings, matches, achievements, level, eggs and items, and what this device remembers. You stay signed in, as a brand-new player."}</span>
              </div>
              <button
                className="app-btn app-btn-ghost"
                onClick={async () => {
                  if (armed !== "reset") { setArmed("reset"); return; }
                  setResetNote("Starting over...");
                  const r = await resetTestAccount();
                  if ("error" in r) { setResetNote("That didn't work (" + r.error + "). Try again."); setArmed(null); }
                }}
              >{armed === "reset" ? "Sure? Start over" : "Start over"}</button>
            </div>
          ) : (
            <div className={styles.settingsRow}>
              <div className={styles.settingsMain}>
                <span className={styles.settingsName}>A test account</span>
                <span className={styles.settingsDesc}>Sign in with the email link as your address plus a tag, like {(user.email || "you@gmail.com").replace("@", "+new@")}. That account can start over as a new player as often as you like.</span>
              </div>
            </div>
          )}
          <div className={styles.settingsRow}>
            <div className={styles.settingsMain}>
              <span className={styles.settingsName}>Play as a new guest</span>
              <span className={styles.settingsDesc}>Sign out and forget everything this device remembers, like a first visit.</span>
            </div>
            <button className="app-btn app-btn-ghost" onClick={() => (armed === "guest" ? void startAsFreshGuest() : setArmed("guest"))}>{armed === "guest" ? "Sure? Start over" : "Start over"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
